import React, { useState, useEffect, useCallback } from 'react';
import assetAPI, {
  Asset,
  AssetStats,
  AlertSummary,
  AssetFilters,
  AssetBYODPolicy,
  AssetAlert
} from '../services/assetAPI';
import AssetList from '../components/Asset/AssetList';
import AssetDetail from '../components/Asset/AssetDetail';
import AssetForm from '../components/Asset/AssetForm';
import DependencyViewer from '../components/Asset/DependencyViewer';
import BYODPolicyView from '../components/Asset/BYODPolicyView';
import {
  Server,
  Shield,
  AlertTriangle,
  Package,
  Download,
  Plus,
  RefreshCw,
  Search,
  Filter,
  FileText,
  Cloud,
  Lock,
  HardDrive,
  CheckCircle,
  XCircle,
  Clock,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

type TabType = 'assets' | 'alerts' | 'dependencies' | 'byod' | 'reports';

const AssetInventory: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('assets');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<AssetStats | null>(null);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);
  const [byodPolicy, setBYODPolicy] = useState<AssetBYODPolicy | null>(null);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);

  const [filters, setFilters] = useState<AssetFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingSBOM, setIsExportingSBOM] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [alerts, setAlerts] = useState<AssetAlert[]>([]);
  const [isGeneratingAlerts, setIsGeneratingAlerts] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [assetsData, statsData, alertSummaryData, policyData, alertsData] = await Promise.all([
        assetAPI.getAssets({ ...filters, search: searchTerm || undefined }),
        assetAPI.getStats(),
        assetAPI.getAlertSummary(),
        assetAPI.getBYODPolicy(),
        assetAPI.getAlerts({ status: 'active' })
      ]);

      setAssets(assetsData);
      setStats(statsData);
      setAlertSummary(alertSummaryData);
      setBYODPolicy(policyData);
      setAlerts(alertsData);
    } catch (error) {
      console.error('Failed to load asset data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssetClick = async (asset: Asset) => {
    try {
      const fullAsset = await assetAPI.getAssetById(asset.id);
      setSelectedAsset(fullAsset);
      setIsDetailOpen(true);
    } catch (error) {
      console.error('Failed to load asset details:', error);
    }
  };

  const handleCreateAsset = () => {
    setEditingAsset(null);
    setIsFormOpen(true);
  };

  const handleEditAsset = (asset: Asset) => {
    setEditingAsset(asset);
    setIsFormOpen(true);
  };

  const handleSaveAsset = async (data: Parameters<typeof assetAPI.createAsset>[0]) => {
    try {
      if (editingAsset) {
        await assetAPI.updateAsset(editingAsset.id, data);
      } else {
        await assetAPI.createAsset(data);
      }
      setIsFormOpen(false);
      setEditingAsset(null);
      loadData();
    } catch (error) {
      console.error('Failed to save asset:', error);
      throw error;
    }
  };

  const handleArchiveAsset = async (asset: Asset, reason: string) => {
    try {
      await assetAPI.archiveAsset(asset.id, reason);
      setIsDetailOpen(false);
      setSelectedAsset(null);
      loadData();
    } catch (error) {
      console.error('Failed to archive asset:', error);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const blob = await assetAPI.exportPDF({ include_dependencies: true, include_byod: true });
      assetAPI.downloadFile(blob, `asset-inventory-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await assetAPI.exportExcel({ include_dependencies: true, include_byod: true });
      assetAPI.downloadFile(blob, `asset-inventory-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Failed to export Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSBOM = async (format: 'json' | 'xml' = 'json') => {
    setIsExportingSBOM(true);
    try {
      const blob = await assetAPI.exportSBOM({ format, include_dev: false });
      const extension = format === 'xml' ? 'xml' : 'json';
      assetAPI.downloadFile(blob, `sbom-cyclonedx-${new Date().toISOString().split('T')[0]}.${extension}`);
    } catch (error) {
      console.error('Failed to export SBOM:', error);
    } finally {
      setIsExportingSBOM(false);
    }
  };

  const handleGenerateAlerts = async () => {
    setIsGeneratingAlerts(true);
    try {
      const result = await assetAPI.generateAlerts();
      console.log(`Generated ${result.generated} alerts`);
      loadData();
    } catch (error) {
      console.error('Failed to generate alerts:', error);
    } finally {
      setIsGeneratingAlerts(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId: number) => {
    try {
      await assetAPI.acknowledgeAlert(alertId);
      loadData();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    const notes = prompt('Enter resolution notes:');
    if (notes !== null) {
      try {
        await assetAPI.resolveAlert(alertId, notes);
        loadData();
      } catch (error) {
        console.error('Failed to resolve alert:', error);
      }
    }
  };

  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'info': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlertTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'review_due': 'Review Due',
      'review_overdue': 'Review Overdue',
      'eol_warning': 'EOL Warning',
      'eol_expired': 'EOL Expired',
      'vulnerability_critical': 'Critical Vulnerability',
      'vulnerability_high': 'High Vulnerability',
      'license_risk': 'License Risk',
      'dependency_outdated': 'Outdated Dependency',
      'encryption_missing': 'Missing Encryption',
      'backup_missing': 'Missing Backup'
    };
    return labels[type] || type;
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'assets', label: 'Assets', icon: <Server size={16} /> },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: <AlertTriangle size={16} />,
      badge: alertSummary?.bySeverity.critical || 0
    },
    { id: 'dependencies', label: 'Dependencies', icon: <Package size={16} /> },
    { id: 'byod', label: 'BYOD Policy', icon: <Cloud size={16} /> },
    { id: 'reports', label: 'Reports', icon: <FileText size={16} /> }
  ];

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'important': return 'text-amber-600 bg-amber-50';
      case 'low_risk': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Server className="text-blue-600" size={24} />
              Asset Inventory
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              SOC 2 CC6.1, CC6.2 | ISO 27001 A.8.1.1, A.8.1.2
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleCreateAsset}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Plus size={16} />
              Add Asset
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Assets</span>
                <Server size={18} className="text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{stats.total}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Critical</span>
                <Shield size={18} className="text-red-500" />
              </div>
              <p className="text-2xl font-semibold text-red-600 mt-1">{stats.byClassification.critical}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Important</span>
                <Shield size={18} className="text-amber-500" />
              </div>
              <p className="text-2xl font-semibold text-amber-600 mt-1">{stats.byClassification.important}</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Encrypted</span>
                <Lock size={18} className="text-green-500" />
              </div>
              <p className="text-2xl font-semibold text-green-600 mt-1">
                {stats.encryption.encrypted}/{stats.total}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Overdue Reviews</span>
                <Clock size={18} className="text-red-500" />
              </div>
              <p className={`text-2xl font-semibold mt-1 ${stats.byReviewStatus.overdue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {stats.byReviewStatus.overdue}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Active Alerts</span>
                <AlertTriangle size={18} className="text-amber-500" />
              </div>
              <p className={`text-2xl font-semibold mt-1 ${(alertSummary?.bySeverity.critical || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {alertSummary?.total || 0}
              </p>
            </div>
          </div>

          {/* Classification Legend Toggle */}
          <div className="mt-4">
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <Info size={16} />
              What do the classification levels mean?
              {showLegend ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {showLegend && (
              <div className="mt-3 bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield size={16} className="text-blue-600" />
                  Asset Classification Guide
                </h3>
                <div className="space-y-3">
                  {/* Critical */}
                  <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        CRITICAL
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">Highest Protection Required</p>
                      <p className="text-xs text-red-700 mt-1">
                        Contains sensitive customer data. A breach would be catastrophic and may result in regulatory penalties,
                        significant financial loss, or severe reputational damage.
                      </p>
                      <p className="text-xs text-red-600 mt-2 font-medium">
                        Examples: Production databases, customer data stores, payment systems
                      </p>
                    </div>
                  </div>

                  {/* Important */}
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        IMPORTANT
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900">Core Business Function</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Essential for business operations but does not store sensitive customer data directly.
                        A breach would be serious but manageable with proper incident response.
                      </p>
                      <p className="text-xs text-amber-600 mt-2 font-medium">
                        Examples: Source code repositories, API services, caching systems
                      </p>
                    </div>
                  </div>

                  {/* Low Risk */}
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        LOW RISK
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900">Limited Impact if Compromised</p>
                      <p className="text-xs text-green-700 mt-1">
                        Non-production environments or assets that contain no customer data.
                        Data is typically regeneratable and a breach would have minimal business impact.
                      </p>
                      <p className="text-xs text-green-600 mt-2 font-medium">
                        Examples: Development environments, test servers, documentation systems
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Info size={12} />
                    Classifications align with SOC 2 CC6.1 (Logical Access) and ISO 27001 A.8.1.1 (Asset Inventory) requirements.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-6 border-b border-gray-200 bg-white">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'assets' && (
          <div className="space-y-4">
            {/* Search & Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg ${
                  showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Filter size={16} />
                Filters
              </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
                    <select
                      value={filters.classification || ''}
                      onChange={(e) => setFilters({ ...filters, classification: e.target.value || undefined })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">All</option>
                      <option value="critical">Critical</option>
                      <option value="important">Important</option>
                      <option value="low_risk">Low Risk</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={filters.asset_type || ''}
                      onChange={(e) => setFilters({ ...filters, asset_type: e.target.value || undefined })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">All Types</option>
                      <option value="infrastructure">Infrastructure</option>
                      <option value="application">Application</option>
                      <option value="service">Service</option>
                      <option value="code_repository">Code Repository</option>
                      <option value="data">Data</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Status</label>
                    <select
                      value={filters.review_status || ''}
                      onChange={(e) => setFilters({ ...filters, review_status: e.target.value || undefined })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">All</option>
                      <option value="current">Current</option>
                      <option value="due_soon">Due Soon</option>
                      <option value="overdue">Overdue</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setFilters({});
                        setSearchTerm('');
                      }}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Asset List */}
            <AssetList
              assets={assets}
              isLoading={isLoading}
              onAssetClick={handleAssetClick}
              onEditAsset={handleEditAsset}
            />
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Asset Alerts</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Automated alerts for review schedules, security issues, and compliance gaps
                </p>
              </div>
              <button
                onClick={handleGenerateAlerts}
                disabled={isGeneratingAlerts}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw size={16} className={isGeneratingAlerts ? 'animate-spin' : ''} />
                {isGeneratingAlerts ? 'Checking...' : 'Check for Alerts'}
              </button>
            </div>

            {/* Alert Summary Cards */}
            {alertSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-600 font-medium">Critical</p>
                  <p className="text-2xl font-bold text-red-700">{alertSummary.bySeverity.critical}</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-600 font-medium">High</p>
                  <p className="text-2xl font-bold text-orange-700">{alertSummary.bySeverity.high}</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-600 font-medium">Medium</p>
                  <p className="text-2xl font-bold text-yellow-700">{alertSummary.bySeverity.medium}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Low</p>
                  <p className="text-2xl font-bold text-blue-700">{alertSummary.bySeverity.low}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600 font-medium">Info</p>
                  <p className="text-2xl font-bold text-gray-700">{alertSummary.bySeverity.info}</p>
                </div>
              </div>
            )}

            {/* Alert List */}
            {alerts.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                <p className="text-lg font-medium text-gray-900">No Active Alerts</p>
                <p className="text-sm text-gray-500 mt-1">
                  All assets are in compliance. Click "Check for Alerts" to scan for issues.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {alerts.map(alert => (
                    <div key={alert.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${
                            alert.alert_severity === 'critical' ? 'bg-red-100' :
                            alert.alert_severity === 'high' ? 'bg-orange-100' :
                            alert.alert_severity === 'medium' ? 'bg-yellow-100' :
                            'bg-blue-100'
                          }`}>
                            <AlertTriangle size={20} className={
                              alert.alert_severity === 'critical' ? 'text-red-600' :
                              alert.alert_severity === 'high' ? 'text-orange-600' :
                              alert.alert_severity === 'medium' ? 'text-yellow-600' :
                              'text-blue-600'
                            } />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getAlertSeverityColor(alert.alert_severity)}`}>
                                {alert.alert_severity.toUpperCase()}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                {getAlertTypeLabel(alert.alert_type)}
                              </span>
                              {alert.asset && (
                                <span className="text-xs text-gray-500">
                                  • {alert.asset.asset_name}
                                </span>
                              )}
                              {alert.dependency && (
                                <span className="text-xs text-gray-500">
                                  • {alert.dependency.package_name}@{alert.dependency.package_version}
                                </span>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-900 mt-1">{alert.alert_title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{alert.alert_message}</p>
                            {alert.due_date && (
                              <p className="text-xs text-gray-500 mt-2">
                                Due: {new Date(alert.due_date).toLocaleDateString()}
                                {alert.days_until_due !== null && (
                                  <span className={alert.days_until_due < 0 ? 'text-red-600 font-medium' : ''}>
                                    {' '}({alert.days_until_due < 0 ? `${Math.abs(alert.days_until_due)} days overdue` : `${alert.days_until_due} days remaining`})
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            Acknowledge
                          </button>
                          <button
                            onClick={() => handleResolveAlert(alert.id)}
                            className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Resolve
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What triggers alerts info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <Info size={16} />
                What triggers alerts?
              </h3>
              <ul className="mt-2 text-sm text-blue-800 space-y-1">
                <li>• <strong>Review Due/Overdue:</strong> Assets approaching or past their scheduled review date</li>
                <li>• <strong>Missing Encryption:</strong> Critical/Important assets without encryption enabled</li>
                <li>• <strong>Missing Backup:</strong> Critical assets without backup configuration</li>
                <li>• <strong>EOL Warning:</strong> Dependencies approaching end-of-life (if scanned)</li>
                <li>• <strong>Vulnerabilities:</strong> Dependencies with known security vulnerabilities (if scanned)</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'dependencies' && (
          <DependencyViewer />
        )}

        {activeTab === 'byod' && byodPolicy && (
          <BYODPolicyView policy={byodPolicy} onUpdate={loadData} />
        )}

        {activeTab === 'reports' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Export Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <FileText className="text-red-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Asset Inventory PDF</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Complete asset inventory report with classifications, encryption status, and review dates.
                    </p>
                    <button
                      onClick={handleExportPDF}
                      disabled={isExporting}
                      className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                    >
                      <Download size={16} />
                      {isExporting ? 'Exporting...' : 'Download PDF'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <FileText className="text-green-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Asset Inventory Excel</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Detailed spreadsheet with all assets, dependencies, and BYOD policy.
                    </p>
                    <button
                      onClick={handleExportExcel}
                      disabled={isExporting}
                      className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                    >
                      <Download size={16} />
                      {isExporting ? 'Exporting...' : 'Download Excel'}
                    </button>
                  </div>
                </div>
              </div>

              {/* SBOM Export - SOC 2 CC6.1 / ISO 27001 A.12.5.1 */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Package className="text-blue-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Software Bill of Materials (SBOM)</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      CycloneDX 1.5 compliant SBOM for supply chain transparency and compliance audits.
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      SOC 2 CC6.1 | ISO 27001 A.12.5.1
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        onClick={() => handleExportSBOM('json')}
                        disabled={isExportingSBOM}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                      >
                        <Download size={16} />
                        {isExportingSBOM ? 'Exporting...' : 'JSON'}
                      </button>
                      <button
                        onClick={() => handleExportSBOM('xml')}
                        disabled={isExportingSBOM}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium disabled:opacity-50"
                      >
                        <Download size={16} />
                        XML
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SBOM Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <Info size={16} />
                What is an SBOM?
              </h3>
              <p className="text-sm text-blue-800 mt-2">
                A Software Bill of Materials (SBOM) is a comprehensive inventory of all software components,
                libraries, and dependencies used in your application. It helps with:
              </p>
              <ul className="mt-2 text-sm text-blue-800 space-y-1">
                <li>• <strong>Supply Chain Security:</strong> Track and verify all third-party components</li>
                <li>• <strong>Vulnerability Management:</strong> Identify affected packages when new CVEs are discovered</li>
                <li>• <strong>License Compliance:</strong> Ensure all licenses are compatible with your policies</li>
                <li>• <strong>Audit Evidence:</strong> Provide auditors with proof of software component tracking</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Asset Detail Modal */}
      {isDetailOpen && selectedAsset && (
        <AssetDetail
          asset={selectedAsset}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedAsset(null);
          }}
          onEdit={() => handleEditAsset(selectedAsset)}
          onArchive={(reason) => handleArchiveAsset(selectedAsset, reason)}
          onRefresh={loadData}
        />
      )}

      {/* Asset Form Modal */}
      {isFormOpen && (
        <AssetForm
          asset={editingAsset}
          onSave={handleSaveAsset}
          onClose={() => {
            setIsFormOpen(false);
            setEditingAsset(null);
          }}
        />
      )}
    </div>
  );
};

export default AssetInventory;
